import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {createReducer, on} from "@ngrx/store";
import * as UnitsActions from "./units.actions";
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {Unit, UnitSearchParams} from '@accounting/_common/models/nomenclature-unit.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const unitFeatureKey = 'unit';

export function sortEntities(a: Unit, b: Unit) {
    return a.name!.localeCompare(b.name!);
}

export const adapter: EntityAdapter<Unit> = createEntityAdapter<Unit>({
    sortComparer: sortEntities
});


export type UnitState = EntityStateModel<Unit, UnitSearchParams>;

export const initialState: UnitState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export const unitReducer = createReducer(
    initialState,

    on(UnitsActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(UnitsActions.LoadListSuccess, (state, action) => {
        return adapter.setAll(action.payload.items, {
            ...state,
            pagination: action.payload.pagination,
            listLoading: undefined
        });
    }),

    on(UnitsActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(UnitsActions.SetFilter, (state, action) => ({
        ...state,
        filter: action.payload
    })),

    on(UnitsActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(UnitsActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(UnitsActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, action.payload);
        if (savedItemAvailableByFilter) {
            return adapter.setOne(action.payload!, {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
            });
        }

        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(UnitsActions.DeleteItem, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, true)
    })),

    on(UnitsActions.DeleteItemSuccess, (state, action) => {
        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(UnitsActions.DeleteItemFailure, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false)
    })),

    on(UnitsActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();

const isAvailableByFilter = (searchParams: UnitSearchParams | undefined, item: Unit): boolean => {

    if (!searchParams) {
        return true;
    }

    if ((searchParams.query && item.name && item.name.toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1) && (searchParams.query && item.nameFull && item.nameFull.toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1)) {
        return false;
    }

    return true;
};
